import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import ChangePasswordIcon from '../../../assets/icons/ChangePassword.svg';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../auth/AuthContext';
import AlertService from '../../../services/AlertService';
import { Skeleton } from 'primereact/skeleton';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import LoginService from '../../../services/LoginService';

const ChangePassword = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const submitControllerRef = useRef(null);
  const curPasswordRef = useRef();
  const newPasswordRef = useRef();
  const confirmPasswordRef = useRef();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    curPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [passwordStrength, setPasswordStrength] = useState('');
  const [strengthColor, setStrengthColor] = useState('');

  const handlePasswordChange = () => {
    const password = newPasswordRef.current.value;
    let strength = 0;

    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[\W_]/.test(password)) strength += 1;

    if (strength <= 2) {
      setPasswordStrength('Weak');
      setStrengthColor('red');
    } else if (strength === 3 || strength === 4) {
      setPasswordStrength('Medium');
      setStrengthColor('orange');
    } else if (strength === 5) {
      setPasswordStrength('Strong');
      setStrengthColor('green');
    } else {
      setPasswordStrength('');
      setStrengthColor('');
    }
  };



const onSubmitHandler = async (e) => {
  e.preventDefault();
  setErrors({ curPassword: "", newPassword: "", confirmPassword: "" });

  const curPasswordValue = curPasswordRef.current.value.trim();
  const newPasswordValue = newPasswordRef.current.value.trim();
  const confirmPasswordValue = confirmPasswordRef.current.value.trim();

  let hasError = false;
  const newErrors = {};

  if (!curPasswordValue) {
    newErrors.curPassword = "Please enter current password.";
    hasError = true;
  }
  if (!newPasswordValue) {
    newErrors.newPassword = "Please enter new password.";
    hasError = true;
  }
  if (!confirmPasswordValue) {
    newErrors.confirmPassword = "Please confirm new password.";
    hasError = true;
  }
  if (
    newPasswordValue &&
    confirmPasswordValue &&
    newPasswordValue !== confirmPasswordValue
  ) {
    newErrors.confirmPassword =
      "New password and confirmation do not match.";
    hasError = true;
  }
  if (passwordStrength === "Weak") {
    newErrors.newPassword = "Password strength is too weak.";
    hasError = true;
  }

  if (hasError) {
    setErrors(newErrors);
    return;
  }

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const payload = {
      password: curPasswordValue,
      newPassword: newPasswordValue,
    };

    const result = await LoginService.ChangePassword(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (result.isSuccess) {
      AlertService.success(
        "Password changed successfully. Please login again."
      );
      navigate("/logout");
    } else {
      AlertService.error(
        result.errorMessage || "Failed to change password."
      );
    }
  } catch (error) {
    if (error.name === "AbortError") return;
    console.error("Error changing password:", error);
    AlertService.error("An unexpected error occurred.");
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
  };
}, []);

  return (
    <Fragment>
      <div className="d-flex container-fluid mt-4 pt-3" style={{overflow:"hidden"}}>
        <div className="password-left d-none d-xl-block d-flex justify-content-center align-items-center mx-auto w-50" style={{ maxHeight: '83vh' }} >
          <img
            src={ChangePasswordIcon}
            alt="Profile"
            className='img-fluid'
          />

        </div>

        <div className="profile-right mt-5 pt-5 mt-xl-0 pt-xl-0 d-flex flex-column justify-content-center">
          {loading ? (
            <div className="w-100 mx-auto" style={{ maxWidth: '550px' }}>
              <center>
                <Skeleton width="40%" height="3rem" className="mb-4" />
              </center>

              <div className="mb-1">
                <Skeleton width="25%" height="3rem" />
              </div>
              <div className="mb-4">
                <Skeleton width="100%" height="3.5rem" />
              </div>

              <div className="mb-1">
                <Skeleton width="20%" height="3rem" />
              </div>
              <div className="mb-4">
                <Skeleton width="100%" height="3.5rem" />
              </div>

              <div className="mb-1">
                <Skeleton width="25%" height="3rem" />
              </div>
              <div className="mb-4">
                <Skeleton width="100%" height="3.5rem" />
              </div>

              <div className="mt-5 d-flex">
                <Skeleton width="20%" height="3.5rem" />
                <Skeleton width="20%" height="3.5rem" className="ms-auto" />
              </div>
            </div>
          ) : (
            <Form onSubmit={onSubmitHandler} className="w-100 mx-auto" style={{ maxWidth: '550px' }} >
              <center><h4 className="mb-4">Change Password</h4></center>

              <Form.Group className="mb-4 position-relative" controlId="currentPassword">
                <Form.Label>Current Password</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showCurrentPassword ? 'text' : 'password'}
                    ref={curPasswordRef}
                    className="border-end-0 custom-input custom-input-color"
                    maxLength={30}
                    style={{ paddingLeft: '5px' }}
                    onChange={() => setErrors(prev => ({ ...prev, curPassword: '' }))}
                  />
                  <span
                    className="input-group-text border-start-0 custom-title-color"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    style={{ cursor: 'pointer', color: '#888' }}
                  >
                    {showCurrentPassword ? <FaEyeSlash className="fs-5" /> : <FaEye className="fs-5" />}
                  </span>
                </div>
                {errors.curPassword && <p className="text-danger fw-normal error-bottom">{errors.curPassword}</p>}
              </Form.Group>

              <Form.Group className="mb-4 position-relative" controlId="newPassword">
                <Form.Label>New Password</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showNewPassword ? 'text' : 'password'}
                    ref={newPasswordRef}
                    className="border-end-0 custom-input custom-input-color"
                    maxLength={30}
                    style={{ paddingLeft: '5px' }}
                    onChange={() => {
                      setErrors(prev => ({ ...prev, newPassword: '' }));
                      handlePasswordChange();
                    }}
                  />
                  <span
                    className="input-group-text border-start-0 custom-title-color"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{ cursor: 'pointer', color: '#888' }}
                  >
                    {showNewPassword ? <FaEyeSlash className="fs-5" /> : <FaEye className="fs-5" />}
                  </span>
                </div>

                {newPasswordRef.current?.value && (
                  <div className="mt-0">
                    <div style={{ height: '8px', borderRadius: '4px', backgroundColor: '#e0e0e0' }}>
                      <div
                        style={{
                          height: '100%',
                          width:
                            passwordStrength === 'Weak'
                              ? '33%'
                              : passwordStrength === 'Medium'
                                ? '66%'
                                : passwordStrength === 'Strong'
                                  ? '100%'
                                  : '0%',
                          backgroundColor: strengthColor,
                          borderRadius: '4px',
                          transition: 'width 0.3s ease-in-out',
                        }}
                      ></div>
                    </div>
                    <div className="text-end" style={{ fontSize: '0.8rem', color: strengthColor }}>
                      {passwordStrength}
                    </div>
                  </div>
                )}

                {errors.newPassword && <p className="text-danger fw-normal error-bottom">{errors.newPassword}</p>}
              </Form.Group>

              <Form.Group className="mb-4 position-relative" controlId="confirmPassword">
                <Form.Label>Confirm New Password</Form.Label>
                <div className="input-group">
                  <Form.Control
                    type={showConfirmPassword ? 'text' : 'password'}
                    ref={confirmPasswordRef}
                    maxLength={30}
                    className="border-end-0 custom-input custom-input-color "
                    style={{ paddingLeft: '5px' }}
                    onChange={() => setErrors(prev => ({ ...prev, confirmPassword: '' }))}
                  />
                  <span
                    className="input-group-text custom-title-color border-start-0"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ cursor: 'pointer', color: '#888' }}
                  >
                    {showConfirmPassword ? <FaEyeSlash className="fs-5" /> : <FaEye className="fs-5" />}
                  </span>
                </div>
                {errors.confirmPassword && <p className="text-danger fw-normal error-bottom">{errors.confirmPassword}</p>}
              </Form.Group>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="outline-secondary"
                  className="fw-bold px-lg-5 py-2"
                  onClick={() => navigate('/profile')}
                  type="button"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="ms-auto px-lg-5 py-2 d-flex align-items-center justify-content-center"
                  type="submit"
                  disabled={loading}
                >
                  Submit
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default ChangePassword;
